<template>
  <div class="ln-page">
    <div class="ln-wrap" :data-view="pageState">
      <header class="ln-head" data-reveal>
        <div class="ln-head__text">
          <p class="ln-eyebrow">Dashboard</p>
          <h1 class="ln-display">Overview</h1>
          <p v-if="lede" class="ln-lede">{{ lede }}</p>
        </div>

        <div class="ln-head__actions">
          <button
            v-if="canWrite"
            type="button"
            class="ln-btn ln-btn--solid"
            @click="openDialogForNewLink()"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
            New Link
          </button>
          <a
            v-if="pageState !== 'new'"
            class="ln-btn ln-btn--quiet"
            :href="userPlan.user.metabase_analytics_url"
            target="_blank"
            rel="noopener"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-chart-bar</v-icon>
            Full analytics
          </a>
        </div>
      </header>

      <!-- ===== zero-link state ===== -->
      <section v-if="pageState === 'new'" class="block" data-reveal>
        <div class="hero">
          <div>
            <p class="ln-eyebrow">Start here</p>
            <h2 class="hero__title">Create your first smart link</h2>
            <p class="hero__text">
              One short link that sends every visitor to the right destination:
              App Store on iPhone, Google Play on Android, and your website on a
              desktop.
            </p>
            <button
              v-if="canWrite"
              type="button"
              class="ln-btn ln-btn--solid ln-btn--lg"
              @click="openDialogForNewLink()"
            >
              <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
              Create your first link
            </button>
            <p v-if="!canWrite" class="hero__text">
              The members of your team can create the first link.
            </p>
          </div>
          <div
            class="hero__demo"
            aria-label="Example of how a smart link routes visitors"
          >
            <div class="hero__demo-url">link-to.app/your-app</div>
            <div class="route">
              <span class="route__who">iPhone</span>
              <v-icon size="14" class="route__arrow">mdi-arrow-right</v-icon>
              <span class="route__to">App Store</span>
            </div>
            <div class="route">
              <span class="route__who">Android</span>
              <v-icon size="14" class="route__arrow">mdi-arrow-right</v-icon>
              <span class="route__to">Google Play</span>
            </div>
            <div class="route">
              <span class="route__who">Desktop</span>
              <v-icon size="14" class="route__arrow">mdi-arrow-right</v-icon>
              <span class="route__to">yourapp.com</span>
            </div>
          </div>
        </div>
        <div class="ideas">
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)"
                >mdi-cellphone-cog</v-icon
              >
            </div>
            <h3>Send people to the right app store</h3>
            <p>
              Paste your App Store and Google Play URLs. Each visitor gets the
              store for their phone.
            </p>
          </article>
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)"
                >mdi-link-variant</v-icon
              >
            </div>
            <h3>Open a screen inside your app</h3>
            <p>
              Deep link straight to a product, profile or offer when the app is
              installed.
            </p>
          </article>
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)">mdi-qrcode</v-icon>
            </div>
            <h3>Put it on print with a QR code</h3>
            <p>
              Every link comes with a QR code for posters, packaging and flyers.
            </p>
          </article>
        </div>
      </section>

      <!-- ===== has links, no clicks yet ===== -->
      <section v-else-if="pageState === 'noclicks'" class="block" data-reveal>
        <div class="share">
          <div>
            <span class="status-pill">No clicks yet</span>
            <h2 class="share__title" style="margin-top: 12px">
              Your link is live. Now it needs visitors.
            </h2>
            <p class="share__text">
              Nobody has opened it in the last {{ overview.range }} days. Put it
              anywhere people will see it; clicks show up here within a few
              minutes.
            </p>
          </div>
          <div class="copybox" v-if="recentLink">
            <span class="copybox__url">{{ recentLink.full_url }}</span>
            <button
              type="button"
              class="ln-btn ln-btn--solid"
              @click="copyRecentLink"
            >
              <v-icon size="small" class="ln-btn__icon">{{
                recentLinkCopied ? "mdi-check" : "mdi-content-copy"
              }}</v-icon>
              {{ recentLinkCopied ? "Copied" : "Copy" }}
            </button>
            <p class="copybox__note">
              Your most recent link.
              {{ recentLink.note ? `Note: "${recentLink.note}".` : "" }}
            </p>
          </div>
        </div>
        <div class="ideas">
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)">mdi-bullhorn</v-icon>
            </div>
            <h3>Use it in a campaign or ad</h3>
            <p>
              One link works for every audience in an ad, newsletter or
              influencer post, whatever phone they use.
            </p>
          </article>
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)">mdi-share</v-icon>
            </div>
            <h3>Share it directly</h3>
            <p>
              Post it in your bio, share it in a story or your email signature.
            </p>
          </article>
          <article class="idea">
            <div class="idea__icon">
              <v-icon size="18" color="var(--ln-blue-fg)">mdi-qrcode</v-icon>
            </div>
            <h3>Print the QR code</h3>
            <p>
              Add it to a poster, menu or product box. QR scans are tracked
              separately from taps.
            </p>
            <QrDownloadMenu v-if="recentLink" :full-url="recentLink.full_url">
              <template #default="{ menuProps, download }">
                <div class="idea__qr">
                  <button
                    type="button"
                    class="ln-inline-link ln-inline-link--button"
                    @click="download()"
                  >
                    Download QR code
                  </button>
                  <button
                    type="button"
                    class="ln-inline-link ln-inline-link--button idea__qr-more"
                    aria-label="Other QR code formats"
                    v-bind="menuProps"
                  >
                    <v-icon size="small">mdi-menu-down</v-icon>
                  </button>
                </div>
              </template>
            </QrDownloadMenu>
          </article>
        </div>
      </section>

      <!-- ===== active: five widgets + range toggle ===== -->
      <section v-else-if="pageState === 'active'" class="block" data-reveal>
        <div class="section-head">
          <div>
            <p class="ln-eyebrow">Traffic</p>
            <p class="section-head__window" aria-live="polite">
              {{ trafficWindowLabel }}
            </p>
          </div>
          <div
            class="rangeseg"
            role="group"
            aria-label="Date range"
            :aria-busy="rangeLoading"
          >
            <button
              v-for="r in [7, 30, 90]"
              :key="r"
              type="button"
              :aria-pressed="r === range"
              @click="setRange(r)"
            >
              <v-progress-circular
                v-if="rangeLoading && r === range"
                class="rangeseg__spinner"
                indeterminate
                size="12"
                width="2"
              />
              {{ r }} days
            </button>
          </div>
        </div>

        <div
          class="widgets"
          :class="{ 'widgets--loading': rangeLoading }"
          :aria-busy="rangeLoading"
        >
          <article class="card w-trend">
            <div class="card__head">
              <p class="ln-tile__label">Clicks per day</p>
            </div>
            <div class="trend__figures">
              <p class="ln-tile__value">
                {{ formatCount(overview.totalClicks) }}
              </p>
              <p class="ln-tile__meta">
                clicks in {{ overview.range }} days ·
                {{ formatCount(avgClicksPerDay) }}
                a day on average
              </p>
            </div>
            <div class="chart">
              <LineChart
                :data="trendChartData"
                :options="trendChartOptions"
                :aria-label="`Clicks per day over the last ${range} days`"
              />
            </div>
          </article>

          <article class="card w-links">
            <div class="card__head">
              <p class="ln-tile__label">Top links</p>
            </div>
            <div style="overflow-x: auto" v-if="overview.topLinks.length">
              <table class="tl">
                <thead>
                  <tr>
                    <th>Link</th>
                    <th>Clicks</th>
                    <th>QR scans</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="link in topLinksRanked" :key="link.linkId">
                    <td>
                      <div class="tl__line">
                        <span class="tl__url"
                          >{{ link.domain }}/{{ link.shortHandle }}</span
                        >
                        <div class="tl__actions">
                          <a
                            v-if="linkAnalyticsUrl(link.linkId)"
                            class="ln-iconbtn"
                            aria-label="See detailed analytics for this link"
                            title="See detailed analytics for this link"
                            :href="linkAnalyticsUrl(link.linkId)"
                            target="_blank"
                            rel="noopener"
                          >
                            <v-icon size="small"
                              >mdi-chart-areaspline-variant</v-icon
                            >
                          </a>
                          <button
                            v-if="canWrite"
                            type="button"
                            class="ln-iconbtn"
                            aria-label="Edit"
                            title="Edit"
                            @click="editLink(link.linkId)"
                          >
                            <v-icon size="small">mdi-pencil</v-icon>
                          </button>
                        </div>
                      </div>
                      <span
                        class="tl__note"
                        :class="{ 'tl__note--none': !link.note }"
                        >{{ link.note || "No note" }}</span
                      >
                      <span class="tl__share"
                        ><span :style="{ width: link.sharePct + '%' }"></span
                      ></span>
                    </td>
                    <td class="tl__clicks">{{ formatCount(link.clicks) }}</td>
                    <td class="tl__qr">
                      {{ link.qrClicks ? formatCount(link.qrClicks) : "–" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="card__hint">No link clicks in this range.</p>
            <div class="card__foot">
              <router-link class="ln-inline-link" to="/links"
                >All links</router-link
              >
            </div>
          </article>

          <article class="card w-devices">
            <div class="card__head">
              <p class="ln-tile__label">Devices</p>
            </div>
            <div class="stack" aria-hidden="true">
              <span
                v-for="d in deviceLegend"
                :key="d.device"
                :style="{ flex: d.clicks, background: d.color }"
              ></span>
            </div>
            <ul class="legend">
              <li v-for="d in deviceLegend" :key="d.device">
                <i :style="{ background: d.color }"></i>
                <span>{{ d.label }}</span>
                <span class="n">{{ formatCount(d.clicks) }}</span>
                <span class="p">{{ d.sharePct }}%</span>
              </li>
            </ul>
          </article>

          <article class="card w-referrers">
            <div class="card__head">
              <p class="ln-tile__label">Top referrers</p>
              <span class="card__hint">Share of all clicks</span>
            </div>
            <ul class="rank">
              <li
                v-for="r in referrersRanked"
                :key="r.source"
                class="rank__row"
              >
                <span class="rank__name"
                  >{{ r.source
                  }}<span v-if="r.source === 'Direct'" class="rank__sub"
                    >No referrer: direct, QR scans</span
                  ></span
                >
                <span class="rank__num"
                  >{{ formatCount(r.clicks)
                  }}<span class="rank__pct">{{ r.sharePct }}%</span></span
                >
                <span class="rank__bar"
                  ><span :style="{ width: r.barPct + '%' }"></span
                ></span>
              </li>
            </ul>
          </article>

          <article class="card w-countries">
            <div class="card__head">
              <p class="ln-tile__label">Top countries</p>
              <span class="card__hint">Share of all clicks</span>
            </div>
            <ul class="rank">
              <li
                v-for="c in countriesRanked"
                :key="c.countryCode"
                class="rank__row"
                :class="{ 'rank__row--unknown': c.countryCode === 'Unknown' }"
              >
                <span class="rank__name">{{ c.countryCode }}</span>
                <span class="rank__num"
                  >{{ formatCount(c.clicks)
                  }}<span class="rank__pct">{{ c.sharePct }}%</span></span
                >
                <span class="rank__bar"
                  ><span :style="{ width: c.barPct + '%' }"></span
                ></span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section id="usage" class="block">
        <div class="section-head">
          <div>
            <p class="ln-eyebrow">Plan usage</p>
            <p class="section-head__window">{{ usageWindowLabel }}</p>
          </div>
        </div>
        <section class="ln-bento">
          <article class="ln-tile ln-tile--wide" data-reveal>
            <p class="ln-tile__label">Plan</p>
            <p class="ln-tile__value ln-tile__value--serif">
              {{ userPlanObject.label }}
            </p>
            <router-link class="ln-inline-link" to="/plan"
              >Upgrade plan</router-link
            >
          </article>

          <article class="ln-tile ln-tile--chart" data-reveal>
            <svg
              v-if="clicksSparklinePath"
              class="ln-tile__sparkline"
              :viewBox="`0 0 ${dailyClicks.length - 1} 40`"
              preserveAspectRatio="none"
            >
              <path :d="clicksSparklinePath" />
            </svg>
            <p class="ln-tile__label">Clicks this month</p>
            <p class="ln-tile__value">
              {{ formatCount(monthlyClicksCounter) }}
            </p>
            <div class="ln-meter" v-if="clicksUsage !== null">
              <span
                v-if="clicksUsage > 0"
                class="ln-meter__fill"
                :style="{ width: clicksUsage + '%' }"
              ></span>
            </div>
            <p class="ln-tile__meta">
              Plan limit
              {{
                userPlanObject.monthly_clicks === -1
                  ? "Unlimited"
                  : formatCount(userPlanObject.monthly_clicks)
              }}
            </p>
          </article>

          <article class="ln-tile" data-reveal>
            <p class="ln-tile__label">Links created</p>
            <p class="ln-tile__value">{{ formatCount(totalLinksCounter) }}</p>
            <div class="ln-meter" v-if="linksUsage !== null">
              <span
                v-if="linksUsage > 0"
                class="ln-meter__fill"
                :style="{ width: linksUsage + '%' }"
              ></span>
            </div>
            <p class="ln-tile__meta">
              Plan limit
              {{
                userPlanObject.max_links === -1
                  ? "Unlimited"
                  : formatCount(userPlanObject.max_links)
              }}
            </p>
          </article>
        </section>
      </section>
    </div>

    <link-dialog ref="linkDialog" />
  </div>
</template>

<script>
import { getOverview, getClicksDaily, listLinks, handleApiError } from "../api";
import { mapState, mapGetters } from "vuex";
import LinkDialog from "../components/LinkDialog.vue";
import QrDownloadMenu from "../components/QrDownloadMenu.vue";
import eventBus from "../eventBus";
import { area, curveMonotoneX } from "d3-shape";
import { Line as LineChart } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
);

const DEVICE_META = {
  ios: { label: "iOS", color: "var(--ln-accent)" },
  android: { label: "Android", color: "var(--ln-green-fg)" },
  desktop: { label: "Desktop", color: "var(--ln-yellow-fg)" },
  huawei: { label: "Huawei", color: "var(--ln-muted)" },
};

// canvas can't resolve CSS custom properties, so the --ln-* tokens (global,
// light-only, set in index.html) are read once per render
function token(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

// "2026-09-03" -> "Sep 3", on the UTC day the backend bucketed it into
function formatDay(date) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default {
  components: {
    LinkDialog,
    QrDownloadMenu,
    LineChart,
  },
  data: () => ({
    overviewLoading: true,
    // sticky: only set from the state-determining fetches (initial load,
    // linkListFetch), never from a plain range switch - otherwise picking a
    // range with no clicks (common: only 893/3,771 users-with-links had a
    // click in the last 30 days per the spec's own numbers) would knock the
    // page out of "active" mid-session, dropping the widgets and the range
    // toggle with no way back short of a reload
    hasClicks: false,
    overview: {
      range: 30,
      totalClicks: 0,
      daily: [],
      topLinks: [],
      topReferrers: [],
      devices: [],
      topCountries: [],
    },
    range: 30,
    // a range switch in flight: the widgets still show overview.range's data.
    // Cleared by whichever overview request is newest when it settles
    rangeLoading: false,
    dailyClicks: [],
    recentLink: null,
    recentLinkCopied: false,
  }),
  async created() {
    // awaited first so state derivation below reads the loaded links_count,
    // but caught: an unhandled rejection here abandoned the rest of this
    // hook, and since App.vue's overlay is bound to store.isLoading, one
    // failing request left the whole app behind the spinner for good
    try {
      await this.$store.dispatch("getUserPlan");
    } catch (err) {
      handleApiError(err);
    }
    await this.fetchOverview({ updateHasClicks: true });
    await this.fetchRecentLinkIfNeeded();
    this.$store.commit("setIsLoading", false);

    this.fetchDailyClicks();
    this._onLinkListFetch = async () => {
      try {
        await this.$store.dispatch("getUserPlan");
      } catch (err) {
        handleApiError(err);
      }
      await this.fetchOverview({ updateHasClicks: true });
      await this.fetchRecentLinkIfNeeded();
    };
    eventBus.on("linkListFetch", this._onLinkListFetch);
  },
  beforeUnmount() {
    if (this._onLinkListFetch) {
      eventBus.off("linkListFetch", this._onLinkListFetch);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  computed: {
    ...mapState(["userPlan"]),
    ...mapGetters([
      "totalLinksCounter",
      "userPlanObject",
      "monthlyClicksCounter",
      "canWrite",
    ]),
    pageState() {
      if (this.overviewLoading) return null;
      if ((this.userPlan.user.links_count || 0) === 0) return "new";
      if (!this.hasClicks) return "noclicks";
      return "active";
    },
    lede() {
      if (this.pageState === "new") {
        return "Create a link to get started. Your traffic will show up here once people open it.";
      }
      if (this.pageState === "noclicks") {
        return "Your links are set up. Once people start opening them, their traffic will show up here.";
      }
      return "";
    },
    trafficWindowLabel() {
      if (this.rangeLoading) return `Loading the last ${this.range} days…`;
      const { range, daily } = this.overview;
      if (!daily.length) return `Last ${range} days`;
      return `Last ${range} days, ${formatDay(daily[0].date)} to ${formatDay(
        daily[daily.length - 1].date,
      )}`;
    },
    usageWindowLabel() {
      // UTC, like the backend's monthly click counter and plan-limit reset
      const now = new Date();
      const month = MONTH_NAMES[now.getUTCMonth()];
      const nextMonth = MONTH_NAMES[(now.getUTCMonth() + 1) % 12];
      return `This calendar month, ${month} ${now.getUTCFullYear()}. Resets on ${nextMonth} 1.`;
    },
    avgClicksPerDay() {
      if (!this.overview.range) return 0;
      return Math.round(this.overview.totalClicks / this.overview.range);
    },
    // null hides the sparkline: an area needs at least 2 points to draw a line
    clicksSparklinePath() {
      const counts = this.dailyClicks.map((d) => d.count);
      const total = counts.reduce((sum, count) => sum + count, 0);
      // below this, a single click's day normalizes to 100% of the user's
      // own max and reads as a dramatic spike instead of "minimal" - hide
      // the chart rather than mislead
      if (counts.length < 2 || total < 10) return null;

      const height = 40;
      // floor of 10 (not 1) so a modest max - e.g. 2 clicks/day - doesn't
      // also get stretched to fill the full height
      const maxCount = Math.max(...counts, 10);

      const sparklineArea = area()
        .x((d, i) => i)
        .y0(height)
        .y1((d) => height - (d / maxCount) * height)
        .curve(curveMonotoneX);

      return sparklineArea(counts);
    },
    clicksUsage() {
      return this.usagePercent(
        this.monthlyClicksCounter,
        this.userPlanObject.monthly_clicks,
      );
    },
    linksUsage() {
      return this.usagePercent(
        this.totalLinksCounter,
        this.userPlanObject.max_links,
      );
    },
    trendChartData() {
      const accent = token("--ln-accent");
      const days = this.overview.daily;
      return {
        labels: days.map((d) => formatDay(d.date)),
        datasets: [
          {
            data: days.map((d) => d.count),
            borderColor: accent,
            borderWidth: 1.75,
            // the token is a 6-digit hex: "17" appends ~9% alpha
            backgroundColor: `${accent}17`,
            fill: "origin",
            tension: 0.3,
            // dots only while there are few enough days to tell them apart
            pointRadius: days.length <= 7 ? 3 : 0,
            pointHoverRadius: 4,
            pointBackgroundColor: token("--ln-surface"),
          },
        ],
      };
    },
    trendChartOptions() {
      const muted = token("--ln-muted");
      const line = token("--ln-line");
      const font = { family: "sans-serif", size: 11 };
      const reducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: reducedMotion ? false : { duration: 400 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          tooltip: {
            displayColors: false,
            callbacks: {
              label: (item) => `${this.formatCount(item.parsed.y)} clicks`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { color: line },
            ticks: { color: muted, font, maxRotation: 0, maxTicksLimit: 7 },
          },
          y: {
            beginAtZero: true,
            grid: { color: line },
            border: { display: false },
            // whole clicks only: no 0.25 / 6.25 ticks on a quiet account
            ticks: { color: muted, font, precision: 0, maxTicksLimit: 5 },
          },
        },
      };
    },
    topLinksRanked() {
      const links = this.overview.topLinks;
      const maxShare = Math.max(...links.map((l) => l.clicks), 1);
      return links.map((l) => ({
        ...l,
        sharePct: (l.clicks / maxShare) * 100,
      }));
    },
    deviceLegend() {
      const devices = this.overview.devices;
      const total = devices.reduce((sum, d) => sum + d.clicks, 0) || 1;
      return devices.map((d) => {
        const meta = DEVICE_META[d.device] || {
          label: d.device,
          color: "var(--ln-muted)",
        };
        return {
          device: d.device,
          label: meta.label,
          color: meta.color,
          clicks: d.clicks,
          sharePct: Math.round((d.clicks / total) * 100),
        };
      });
    },
    referrersRanked() {
      return this.rankedShares(this.overview.topReferrers);
    },
    countriesRanked() {
      return this.rankedShares(this.overview.topCountries);
    },
  },
  watch: {
    pageState(value) {
      if (!value) return;
      this.$nextTick(() => this.setupReveal());
    },
  },
  methods: {
    async fetchOverview({ updateHasClicks = false } = {}) {
      const range = this.range;
      // only the newest request counts: a later range click, or the refresh
      // after a link is saved, supersedes whatever is still in flight
      const seq = (this._overviewSeq = (this._overviewSeq || 0) + 1);
      try {
        const overview = await getOverview(range);
        if (seq !== this._overviewSeq) return;
        this.overview = overview;
        if (updateHasClicks) {
          this.hasClicks = overview.totalClicks > 0;
        }
      } catch (err) {
        handleApiError(err);
        if (seq !== this._overviewSeq) return;
        // the toggle shows a range the widgets don't: put it back on the
        // range whose data is still on screen
        if (this.range !== this.overview.range) {
          this.range = this.overview.range;
          this.$store.commit("showSnackbarMessage", {
            content: `Couldn't load the last ${range} days. Please try again.`,
            color: "error",
          });
        }
      } finally {
        this.overviewLoading = false;
        if (seq === this._overviewSeq) this.rangeLoading = false;
      }
    },
    // the overview response has no link in it for this state (its topLinks
    // is empty), so fetch the newest link with the existing list call - only
    // needed here, no backend change
    async fetchRecentLinkIfNeeded() {
      if ((this.userPlan.user.links_count || 0) === 0) return;
      if (this.hasClicks) return;
      try {
        const res = await listLinks({ take: 1, skip: 0 });
        this.recentLink = res.result[0] || null;
      } catch (err) {
        handleApiError(err);
      }
    },
    async fetchDailyClicks() {
      try {
        const res = await getClicksDaily();
        this.dailyClicks = res.days;
      } catch (err) {
        handleApiError(err);
      }
    },
    setRange(range) {
      if (range === this.range) return;
      this.range = range;
      this.rangeLoading = true;
      return this.fetchOverview();
    },
    rankedShares(rows) {
      const total = rows.reduce((sum, r) => sum + r.clicks, 0) || 1;
      const maxClicks = Math.max(...rows.map((r) => r.clicks), 1);
      return rows.map((r) => ({
        ...r,
        sharePct: Math.round((r.clicks / total) * 100),
        barPct: (r.clicks / maxClicks) * 100,
      }));
    },
    copyRecentLink() {
      if (!this.recentLink) return;
      navigator.clipboard
        .writeText(this.recentLink.full_url)
        .then(() => {
          this.recentLinkCopied = true;
          setTimeout(() => {
            this.recentLinkCopied = false;
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy URL: ", err);
        });
    },
    // null hides the meter: -1 is the "unlimited" sentinel, and both counters
    // are undefined until getUserPlan resolves
    usagePercent(used, limit) {
      if (typeof limit !== "number" || limit <= 0) return null;
      if (typeof used !== "number") return null;
      return Math.min(100, Math.round((used / limit) * 100));
    },
    formatCount(value) {
      return typeof value === "number" ? value.toLocaleString() : value;
    },
    linkAnalyticsUrl(linkId) {
      const url = this.userPlan.user.metabase_analytics_url;
      if (!url) return undefined;
      return url.replace("#", "?link_id=" + linkId + "#");
    },
    editLink(id) {
      const linkDialog = this.$refs.linkDialog;
      linkDialog.init(id);
      linkDialog.open();
    },
    openDialogForNewLink() {
      const linkDialog = this.$refs.linkDialog;
      linkDialog.init();
      linkDialog.open();
    },
    setupReveal() {
      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      if (this._revealObserver) {
        this._revealObserver.disconnect();
      }
      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  display: flex;
  flex-direction: column;
  padding: 20px 16px 40px;
}

.ln-wrap > .block {
  order: 1;
}

.ln-wrap > #usage {
  order: 2;
}

/* active accounts: usage tiles sit above the traffic, as they do on /links today */
.ln-wrap[data-view="active"] > #usage {
  order: 0;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

.ln-bento [data-reveal]:nth-child(2) {
  transition-delay: 80ms;
}

.ln-bento [data-reveal]:nth-child(3) {
  transition-delay: 160ms;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.ln-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ---------- buttons ---------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-page a.ln-btn--quiet {
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--lg {
  height: 44px;
  padding: 0 20px;
  font-size: 14px;
}

.ln-inline-link {
  align-self: flex-start;
  margin-top: auto;
  padding-top: 14px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ln-ink-2);
  text-decoration: none;
  border-bottom: 1px solid var(--ln-line);
  transition:
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-page a.ln-inline-link {
  color: var(--ln-ink-2);
}

.ln-inline-link--button {
  background: none;
  border-top: 0;
  border-left: 0;
  border-right: 0;
  border-radius: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}

.ln-inline-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

/* ---------- section head ------------------------------------------------ */

.section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0 0 12px;
}

.section-head .ln-eyebrow {
  margin: 0;
}

.section-head__window {
  font-size: 12px;
  color: var(--ln-muted);
  margin: 4px 0 0;
}

.block {
  margin-bottom: 28px;
}

/* ---------- bento stats ------------------------------------------------ */

.ln-bento {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}

.ln-tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.ln-tile--wide {
  grid-column: span 2;
}

.ln-tile--chart {
  position: relative;
  z-index: 0;
  overflow: hidden;
}

.ln-tile__sparkline {
  position: absolute;
  z-index: -1;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: ln-sparkline-fade-in 400ms var(--ln-ease);
}

.ln-tile__sparkline path {
  fill: var(--ln-line-soft);
  stroke: var(--ln-muted);
  stroke-width: 0.6;
  vector-effect: non-scaling-stroke;
}

@keyframes ln-sparkline-fade-in {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ln-tile__sparkline {
    animation: none;
  }
}

.ln-tile__label {
  margin: 0 0 14px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
  word-break: normal;
}

.ln-tile__value {
  margin: 0;
  font-family: sans-serif;
  font-size: 30px;
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
  font-variant-numeric: tabular-nums;
}

.ln-tile__value--serif {
  font-family: sans-serif;
  font-weight: 400;
  letter-spacing: -0.03em;
}

.ln-tile__meta {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ln-muted);
}

.ln-meter {
  height: 2px;
  margin-top: 14px;
  background: var(--ln-line);
  overflow: hidden;
}

.ln-meter__fill {
  display: block;
  min-width: 3px;
  height: 100%;
  background: var(--ln-ink);
  transition: width 600ms var(--ln-ease);
}

/* ---------- zero-link hero ---------------------------------------------- */

.hero {
  display: grid;
  gap: 24px;
  padding: 28px 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface-2);
}

.hero__title {
  margin: 0;
  font-family: sans-serif;
  font-size: 26px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
}

.hero__text {
  margin: 10px 0 20px;
  max-width: 58ch;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.hero__demo {
  display: grid;
  gap: 8px;
  align-content: center;
  padding: 18px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  font-size: 13px;
}

.hero__demo-url {
  font-weight: 500;
  color: var(--ln-ink);
  padding-bottom: 10px;
  margin-bottom: 4px;
  border-bottom: 1px dashed var(--ln-line);
}

.route {
  display: grid;
  grid-template-columns: 76px 16px 1fr;
  align-items: center;
  gap: 8px;
  color: var(--ln-ink-2);
}

.route__who {
  color: var(--ln-muted);
  font-size: 12px;
}

.route__arrow.v-icon {
  color: var(--ln-muted);
}

.route__to {
  overflow-wrap: anywhere;
}

.ideas {
  display: grid;
  gap: 12px;
  margin-top: 12px;
}

.idea {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.idea__icon {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: var(--ln-blue-bg);
}

.idea h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--ln-ink);
}

.idea p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ln-muted);
}

.idea .ln-inline-link {
  padding-top: 10px;
}

/* the download link and its format arrow read as one split control */
.idea__qr {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-top: auto;
}

.idea__qr .ln-inline-link {
  margin-top: 0;
}

.idea__qr-more {
  min-width: 24px;
  padding-left: 4px;
  padding-right: 4px;
}

/* ---------- no-clicks state --------------------------------------------- */

.share {
  display: grid;
  gap: 20px;
  padding: 24px 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface-2);
}

.share__title {
  margin: 0;
  font-family: sans-serif;
  font-size: 22px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
}

.share__text {
  margin: 8px 0 0;
  max-width: 60ch;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.copybox {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 14px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface);
  max-width: 560px;
}

.copybox__url {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.copybox__note {
  flex-basis: 100%;
  margin: 0;
  font-size: 12px;
  color: var(--ln-muted);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--ln-yellow-bg);
  color: var(--ln-yellow-fg);
  font-size: 11px;
  font-weight: 500;
}

.status-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* ---------- populated widgets -------------------------------------------- */

.rangeseg {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border: 1px solid var(--ln-line);
  border-radius: 8px;
  background: var(--ln-surface-2);
}

.rangeseg button {
  border: 0;
  border-radius: 5px;
  padding: 6px 12px;
  background: transparent;
  color: var(--ln-muted);
  font-family: sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rangeseg__spinner {
  color: var(--ln-muted);
}

.rangeseg button[aria-pressed="true"] {
  background: var(--ln-surface);
  color: var(--ln-ink);
  box-shadow: 0 0 0 1px var(--ln-line);
}

.rangeseg button:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.widgets {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  transition: opacity 200ms var(--ln-ease);
}

.widgets--loading {
  opacity: 0.5;
}

@media (prefers-reduced-motion: reduce) {
  .widgets {
    transition: none;
  }
}

.card {
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.card__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
}

.card__head .ln-tile__label {
  margin: 0;
}

.card__hint {
  font-size: 12px;
  color: var(--ln-muted);
}

.trend__figures {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 20px;
  margin-bottom: 12px;
}

.trend__figures .ln-tile__meta {
  margin: 0;
}

.chart {
  position: relative;
  height: 220px;
}

/* ranked lists */

.rank {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 14px;
}

.rank__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 12px;
  align-items: baseline;
}

.rank__name {
  min-width: 0;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.rank__sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--ln-muted);
}

.rank__num {
  text-align: right;
  color: var(--ln-ink);
  font-variant-numeric: tabular-nums;
}

.rank__pct {
  display: inline-block;
  min-width: 3.2em;
  margin-left: 8px;
  color: var(--ln-muted);
  font-size: 12px;
}

.rank__bar {
  grid-column: 1 / -1;
  height: 2px;
  background: var(--ln-line);
}

.rank__bar span {
  display: block;
  height: 100%;
  background: var(--ln-ink);
}

.rank__row--unknown .rank__name {
  color: var(--ln-muted);
  font-style: italic;
}

.rank__row--unknown .rank__bar span {
  background: var(--ln-muted);
}

/* top links table */

.tl {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}

.tl th {
  padding: 0 0 8px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
  text-align: right;
  border-bottom: 1px solid var(--ln-line);
}

.tl th:first-child {
  text-align: left;
}

.tl td {
  padding: 12px 0;
  border-bottom: 1px solid var(--ln-line);
  text-align: right;
  vertical-align: top;
}

.tl tr:last-child td {
  border-bottom: 0;
}

.tl td:first-child {
  text-align: left;
  padding-right: 16px;
}

.tl__line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 6px;
}

.tl__actions {
  display: flex;
  gap: 2px;
  margin: -4px 0;
  opacity: 0;
  transition: opacity 150ms var(--ln-ease);
}

.tl tr:hover .tl__actions,
.tl__actions:focus-within {
  opacity: 1;
}

/* touch screens have no hover: keep the icons visible */
@media (hover: none) {
  .tl__actions {
    opacity: 1;
  }
}

.ln-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface);
  color: var(--ln-muted);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-page a.ln-iconbtn {
  color: var(--ln-muted);
}

.ln-iconbtn:hover {
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-iconbtn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.ln-iconbtn .v-icon {
  font-size: 17px;
  color: inherit;
}

.tl__url {
  color: var(--ln-accent);
  font-weight: 500;
  overflow-wrap: anywhere;
}

.tl__note {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--ln-muted);
}

.tl__note--none {
  font-style: italic;
}

.tl__clicks {
  color: var(--ln-ink);
  padding-left: 16px;
}

.tl__qr {
  color: var(--ln-muted);
  padding-left: 16px;
  width: 1%;
  white-space: nowrap;
}

.tl__share {
  display: block;
  height: 2px;
  margin-top: 8px;
  background: var(--ln-line);
}

.tl__share span {
  display: block;
  height: 100%;
  background: var(--ln-ink);
}

.card__foot {
  margin-top: 14px;
}

/* devices */

.stack {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  gap: 2px;
  margin: 4px 0 18px;
}

.stack span {
  display: block;
  height: 100%;
}

.legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.legend li {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  font-variant-numeric: tabular-nums;
}

.legend i {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.legend .n {
  color: var(--ln-ink);
  text-align: right;
}

.legend .p {
  color: var(--ln-muted);
  font-size: 12px;
  min-width: 3.4em;
  text-align: right;
}

/* ---------- breakpoints ------------------------------------------------- */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-bento {
    grid-template-columns: 1.15fr 1fr 1fr;
    gap: 16px;
  }

  .ln-tile {
    padding: 26px;
  }

  .ln-tile--wide {
    grid-column: auto;
  }

  .ln-tile__value {
    font-size: 36px;
  }

  .ideas {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .widgets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .w-trend,
  .w-links {
    grid-column: 1 / -1;
  }

  .card {
    padding: 24px;
  }

  .hero,
  .share {
    padding: 32px;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }

  .ln-head {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .hero {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: center;
  }

  .widgets {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }

  .w-trend {
    grid-column: span 12;
  }

  .w-links {
    grid-column: span 8;
  }

  .w-devices {
    grid-column: span 4;
  }

  .w-referrers,
  .w-countries {
    grid-column: span 6;
  }
}
</style>
